import { createContext, useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import type { UseIntersectionObserverOpts } from '../useIntersectionObserver/useIntersectionObserver';
import { useIntersectionObserver } from '../useIntersectionObserver/useIntersectionObserver';

export type SkeletonMeasurements = Map<MeasurementId, DOMRectReadOnly>;

export const SkeletonContext = createContext<{
  measurements: SkeletonMeasurements;
  disableSkeletons: boolean;
}>({
  measurements: new Map(),
  disableSkeletons: true,
});

const SkeletonContextProvider: React.FC<
  React.PropsWithChildren<{ disableSkeletons?: boolean }>
> = ({ children, disableSkeletons = false }) => {
  const [measurements] = useState(
    () => new Map<MeasurementId, DOMRectReadOnly>()
  );

  const value = useMemo(
    () => ({
      measurements,
      disableSkeletons,
    }),
    [measurements, disableSkeletons]
  );

  return (
    <SkeletonContext.Provider value={value}>
      {children}
    </SkeletonContext.Provider>
  );
};

type UseSkeletonContextOpts = Omit<UseIntersectionObserverOpts, 'callbackMode'>;

type SkeletonContextProviderProps = {
  disableSkeletons?: boolean;
  children: React.ReactNode;
};

export const useSkeletonContext = (opts: UseSkeletonContextOpts = {}) => {
  const { IOSubscriptionProvider, viewportRef } = useIntersectionObserver({
    callbackMode: true,
    ...opts,
  });

  const Provider = useCallback<React.FC<SkeletonContextProviderProps>>(
    ({ children, ...rest }) => (
      <IOSubscriptionProvider>
        <SkeletonContextProvider {...rest}>{children}</SkeletonContextProvider>
      </IOSubscriptionProvider>
    ),
    [IOSubscriptionProvider]
  );

  return {
    viewportRef,
    SkeletonContextProvider: Provider,
  };
};

export type MeasurementId = string | number;
